import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Fade, Modal as MuiModal, Slide } from '@mui/material';
import classNames from 'classnames/bind';
import React from 'react';
import useMobileMediaQuery from '../../hooks/useMobileMediaQuery';
import IconButton from './IconButton';

import styles from './Modal.module.scss';

const cx = classNames.bind(styles);

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg' | 'full';
  /** Presentation below the mobile breakpoint. `inherit` keeps `size`. */
  mobile?: 'inherit' | 'full';
  transition?: 'none' | 'fade' | 'slide-left' | 'slide-up';
  title?: React.ReactNode;
  footer?: React.ReactNode;
  showCloseButton?: boolean;
  /** Remove body padding, for galleries and maps that bleed to the edge. */
  flush?: boolean;
  /** Accessible name when there is no visible `title`. */
  ariaLabel?: string;
  className?: string;
  children: React.ReactNode;
};

/**
 * The app's modal shell.
 *
 * Owns the mobile/desktop fork internally, so callers no longer branch on
 * `isMobile` to pick between two `<Modal>` trees.
 *
 * It also stops click and keydown propagation at the container. Every one of
 * these modals can be opened from inside a draggable card, and dnd-kit
 * listeners on the ancestor otherwise swallow typing and clicks — a bug
 * previously patched by hand in CardTagsModal only.
 */
function Modal({
  open,
  onClose,
  size = 'md',
  mobile = 'inherit',
  transition = 'fade',
  title,
  footer,
  showCloseButton = true,
  flush = false,
  ariaLabel,
  className,
  children,
}: ModalProps) {
  const isMobile = useMobileMediaQuery();
  const effectiveSize = isMobile && mobile === 'full' ? 'full' : size;

  const container = (
    <div
      className={cx('container', effectiveSize, { centered: effectiveSize !== 'full' }, className)}
      aria-label={title ? undefined : ariaLabel}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {(title || showCloseButton) && (
        <div className={cx('header')}>
          {title ? <h2 className={cx('title')}>{title}</h2> : null}
          {showCloseButton && (
            <IconButton
              className={cx('close')}
              label='Close'
              icon={<CloseRoundedIcon />}
              onClick={onClose}
              tooltip={false}
            />
          )}
        </div>
      )}
      <div className={cx('body', { bodyFlush: flush })}>{children}</div>
      {footer ? <div className={cx('footer')}>{footer}</div> : null}
    </div>
  );

  return (
    <MuiModal
      open={open}
      onClose={onClose}
    >
      {transition === 'none' ? (
        container
      ) : transition === 'fade' ? (
        <Fade in={open}>{container}</Fade>
      ) : (
        <Slide
          direction={transition === 'slide-left' ? 'left' : 'up'}
          in={open}
          mountOnEnter
          unmountOnExit
        >
          {container}
        </Slide>
      )}
    </MuiModal>
  );
}

Modal.displayName = 'Modal';
export default Modal;
